// クラシック版のカテゴリのボタンと、キャラのデータを受け取ったあとの用意（段階 C の2 の 4。2026-10-25 に chibi.js の setCategory から描き直した。見た目・動きは前と同じ）。
// 起動の init が呼ぶ。カテゴリのボタン（svg#chaSvgCategory の rect.chibiCategoryBtnR#chibiCategoryBtnR<番号>・g.g_root の中の絵と押す所 rect.chibiCategoryBtn#chibiCategoryBtn<番号>・
// 選んでいる帯 rect#chibiCategorySelected）を作り、パーツを展開して、キャラのレイヤーの g・名前とコピーライトの文字・保存用の defs・柄の一覧・色の列・グラデーションのバーを用意する。
// カテゴリのボタンのクリックは d3 で付ける（起動の処理が d3.select("#chibiCategoryBtn0").on("click")() で取り出して、引数なしで呼ぶ＝先頭のカテゴリを選び直す。呼ぶ所を描き直したら dom.ts の onClick に）
import type { Category } from '../types.js';
import { chibi, common, zlib } from './engine.js';
import { addClass, appendSvg, bindData, hasClass, removeClass, svgEl } from './dom.js';
import * as fx from './fx.js';
import type { Selection } from './selection.js';
import { tooltip } from './tooltip.js';

/** カテゴリのボタンの位置（setCategory が入れる） */
type PosCategory = Category & { Pos: { X: number; Y: number; W: number; H: number }; IS_SWIDTH?: number };
/** init が渡すキャラのデータ（getcategory.php の返事。使い終わった大きいものは null にする） */
interface ClassicCategoryData {
  CATEGORY: PosCategory[];
  LAYER: string[];
  SWITCH: string | null;
  PALLET: string[][] | null;
  CHARA: unknown;
  CACHE: string | null;
  PATTERN?: string | null;
}
interface Scale {
  x(v: number): number;
  y(v: number): number;
}
interface Button {
  ENABLED(valid: boolean): void;
}
interface CategoryEngine {
  isMobile: boolean;
  isCompress: boolean;
  isUseCache: boolean;
  CACHE_KEY_PARTS: string;
  DIV_F_CATEGORY: string;
  DIV_F_PARTS_SC: string;
  DIV_F_MAIN: string;
  SVG_CATEGORY: string;
  SVG_CLIP: string;
  SVG_TEXT: string;
  SVG_MAKE: string;
  SVG_SAVE: string;
  SVG_BGSAVE: string;
  ATTR_G_ROOT: string;
  ATTR_SELECTED: string;
  ATTR_LAYER: string;
  ATTR_AUTOSAVEKEY: string;
  ATTR_EDITFLOATBUTTON: string;
  PARTS_BG: string;
  PARTS_CHARA: string;
  PARTS_TIPS1: string;
  SIZE_CATEGORY_ICON: number;
  SIZE_CATEGORY_IMAGE: number;
  SIZE_CHARAT_IMAGE: number;
  SIZE_PARTS_ICON: number;
  SIZE_PARTS_ROW: number;
  SIZE_COLOR_ICON_W: number;
  SIZE_COLOR_ICON_H: number;
  SCALE_MAKE: number;
  MODE_TEXT: boolean;
  MODE_CLIP: boolean;
  MODE_FILTER: boolean;
  MODE_PATTERN: unknown;
  STROKE_WIDTH: unknown;
  FONT_NAME: string;
  FONT_NAME_SRC: string;
  FONT_CR: string;
  COLOR_ACTIVE: string;
  COLOR_NAME: string;
  COLOR_NAME_WM: string;
  COLOR_CR: string;
  CompressTime: number;
  scCategory: Scale;
  scParts: Scale;
  scColor: Scale;
  scMake: Scale;
  svgCategory: Selection;
  svgMake: Selection;
  svgBg: Selection;
  selLayer: Selection;
  selLayer2: Selection | null;
  selLayer3: Selection | null;
  selCategory: Category;
  groupCategory: Category[];
  excCategory: Category[];
  allCategory: Record<string, Category>;
  categoryData: ClassicCategoryData;
  partsCache: Record<string, { SVG: string }>;
  pallet: unknown[];
  charaDef: unknown;
  patternList: ArrayLike<Element> & { [i: number]: Element };
  tips1: { show(): void } | null;
  btnEditLR: Button;
  btnSubMove: Button;
  btnSubReverse: Button;
  btnSubExpand: Button;
  btnSubContract: Button;
  btnSubThick: Button;
  btnSubThin: Button;
  btnEditParts: { ON_PARTS(open?: boolean): void; SW_PARTS(): void };
  config<T>(key: string, def: T): T;
  loadDB(useCache: boolean, key: string, callback: (data: string | null) => void): void;
  saveDB(key: string, value: string): void;
  saveChara(key: string): void;
  setPartsList(...args: unknown[]): void;
  selColor(cat: Category): void;
  getGroupCategory(all: Category[], sel: Category): Category[];
  getExcludeCategory(all: Category[], sel: Category): Category[];
  getSelLayerId(): number;
  setTextArea(): void;
  setPatternList(list: ArrayLike<Element>, w: number, h: number): void;
  setColorList(pallet: unknown[], w: number, h: number): void;
  setGradBar(): void;
}
const E = (): CategoryEngine => chibi() as unknown as CategoryEngine;
type Common = {
  getXYfromIndex(i: number, row: number, w: number, h: number, wmax: number, horizon: boolean): { X: number; Y: number; W?: number; H?: number };
  getGfromSVG(svgText: string, id: string): Element;
  getAllGfromSVG(svgText: string | null | undefined): ArrayLike<Element> & { [i: number]: Element };
};
const C = (): Common => common() as unknown as Common;
const XLINK = 'http://www.w3.org/1999/xlink';

/**
 * カテゴリのボタンを作り、キャラのデータ（category）を受け取ったあとの用意をして aCallback を呼ぶ。
 * aW・aH: カテゴリのボタン 1 つ分の大きさ、aRow: 段の数
 */
export function setCategory(category: ClassicCategoryData, aW: number, aH: number, aRow: number, aCallback?: () => void): void {
  const e = E();
  const btnExp = e.config('CATEGORY_BTN_EXP', 1.2); // カテゴリのボタンの幅の倍率（元ソースの bowl・storm は 1.4）
  const svgCat = appendSvg(e.DIV_F_CATEGORY, e.SVG_CATEGORY, category.CATEGORY.length * e.scCategory.x(e.SIZE_CATEGORY_ICON) * btnExp, e.scCategory.y(e.SIZE_CATEGORY_ICON));
  e.svgCategory = fx.d3Select(svgCat);
  // 幅の上限（既定 700。旧画面は上限なし = 0）
  const maxWidth = e.config('CATEGORY_MAX_W', 700);
  if (svgCat && maxWidth && Number(svgCat.getAttribute('width')) > maxWidth) svgCat.setAttribute('width', String(maxWidth));
  const st1 = new Date().getTime(); // 展開にかかった時間（CompressTime）
  const pWidthMax = Math.ceil(category.CATEGORY.length / aRow); // 1 段に並ぶボタンの数
  const pad = { X: (aW * btnExp) / 14, Y: 0 };
  // カテゴリ名 → カテゴリ。保存データ（画像からの復元・#chara= では、ほかの人が作ったものも入る）のカテゴリ名で引くので、
  // __proto__ などが Object の中身に当たらないようにプロトタイプなしのオブジェクトにする（2026-10-22）
  e.allCategory = Object.create(null);
  // キャラのデータそのもの（中身 charat.core.min.js に渡す。chibi.core）
  e.categoryData = category;
  e.loadDB(e.isUseCache, e.CACHE_KEY_PARTS, (partsData) => {
    const en = E();
    // パーツの展開（ブラウザに保存したものがあればそれ、無ければ category.CACHE を展開して保存）
    if (partsData != null) {
      en.partsCache = JSON.parse(partsData);
    } else {
      en.partsCache = en.isCompress ? inflateCache(category.CACHE!) : JSON.parse(category.CACHE!);
      en.saveDB(en.CACHE_KEY_PARTS, JSON.stringify(en.partsCache));
    }
    en.pallet = category.PALLET!;
    en.pallet.splice(0, 0, '#000000'); // 先頭に「元の色に戻す」用の黒
    en.charaDef = category.CHARA;
    en.CompressTime = new Date().getTime() - st1;
    if (svgCat) makeButtons(svgCat, category, aW, aH, aRow, btnExp, pWidthMax, pad);
    makeLayers(category);
    makeTexts();
    makeDefs();
    // 柄の一覧（柄の SVG から g を取り出し、id を 3 桁の柄番号にそろえる）
    if (en.MODE_PATTERN) {
      en.patternList = C().getAllGfromSVG(category.PATTERN);
      for (let i = 0; i < en.patternList.length; i++) en.patternList[i].id = en.patternList[i].id.substring(1, 4);
      en.setPatternList(en.patternList, en.scColor.x(en.SIZE_COLOR_ICON_W), en.scColor.y(en.SIZE_COLOR_ICON_H));
    }
    // 色の列（色のボタンの幅に足す分 COLOR_ICON_ADD。旧画面は 0）・グラデーションのバー
    en.setColorList(en.pallet, en.scColor.x(en.SIZE_COLOR_ICON_W + en.config('COLOR_ICON_ADD', 5)), en.scColor.y(en.SIZE_COLOR_ICON_H));
    en.setGradBar();
    // メモリを食う使い終わったデータは捨てる
    category.PATTERN = null;
    category.SWITCH = null;
    category.PALLET = null;
    category.CACHE = null;
    // ポーズを変えるときの注意の吹き出し（dressup の POSE_TIPS）。素体のカテゴリ（PARTS_TIPS1）を選んだときに出す
    if (en.config('POSE_TIPS', false)) {
      tooltip('g.' + en.ATTR_EDITFLOATBUTTON, {
        content: typeof _t !== 'undefined' && _t && _t.pose_warning ? _t.pose_warning : 'When changing pose, display may break if sleeves/gloves are not turned off.',
        placement: 'left',
        animation: 'shift-away',
        duration: 100,
        arrow: true,
        arrowType: 'round',
        trigger: 'manual',
        theme: 'translucent',
        maxWidth: Number(en.svgMake.attr('width')) * 0.58 + 'px',
        onHide: () => {
          E().tips1 = null;
        },
      });
      en.tips1 = (document.querySelector('g.' + en.ATTR_EDITFLOATBUTTON) as Element & { _tippy: { show(): void } })._tippy;
    }
    if (aCallback != undefined) aCallback();
  });
}

/** 圧縮したパーツ（base64 → zlib を展開 → 1 バイトを 1 文字に → JSON）。前と同じく 2048 バイトずつ文字にする */
function inflateCache(cache: string): Record<string, { SVG: string }> {
  const raw = window.atob(cache);
  const bytes = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
  const buf = new (zlib().Inflate)(bytes).decompress();
  const chunkSize = 2048;
  const strList: string[] = [];
  for (let pos = 0; pos < buf.byteLength; pos += chunkSize) {
    strList.push(String.fromCharCode.apply('', new Uint16Array(buf.slice(pos, pos + chunkSize)) as unknown as number[]));
  }
  return JSON.parse(strList.join(''));
}

/** カテゴリのボタン（背景の枠・絵・押す所）と、選んでいるカテゴリの帯 */
function makeButtons(svg: SVGSVGElement, category: ClassicCategoryData, aW: number, aH: number, aRow: number, btnExp: number, pWidthMax: number, pad: { X: number; Y: number }): void {
  const e = E();
  // 背景の枠（位置 d.Pos を決め、chibi.allCategory に名前 → カテゴリで入れる）
  category.CATEGORY.forEach((d, i) => {
    const p = C().getXYfromIndex(i, aRow, aW * btnExp, aH, pWidthMax, true);
    p.X += pad.X;
    p.Y += pad.Y;
    p.W = aW * btnExp;
    p.H = aH - pad.Y * 2;
    d.Pos = p as PosCategory['Pos'];
    e.allCategory[d.ALIAS === '' ? d.NAME : d.ALIAS] = d;
    const r = svgEl('rect', { class: 'chibiCategoryBtnR', id: 'chibiCategoryBtnR' + i, x: d.Pos.X, y: d.Pos.Y, width: d.Pos.W, height: d.Pos.H });
    bindData(r, d);
    svg.appendChild(r);
  });
  // カテゴリの絵（SWITCH の SVG から、カテゴリ名＝ALIAS（空なら NAME）の g を取り出して置く）
  const roots = category.CATEGORY.map((d) => {
    const g = svgEl('g', { class: e.ATTR_G_ROOT });
    bindData(g, d);
    svg.appendChild(g);
    const img = C().getGfromSVG(category.SWITCH!, d.ALIAS === '' ? d.NAME : d.ALIAS);
    g.appendChild(img);
    bindData(img, d);
    img.setAttribute('transform', 'translate(' + (d.Pos.X + (d.Pos.W - aW) / 2 - pad.X) + ', ' + d.Pos.Y + '), scale(' + aW / e.SIZE_CATEGORY_IMAGE + ',' + d.Pos.H / e.SIZE_CATEGORY_IMAGE + ')');
    return g;
  });
  // 押す所（透明な rect。押したカテゴリのパーツの一覧・色・編集のボタンに切りかえる）
  category.CATEGORY.forEach((d, i) => {
    const btn = svgEl('rect', { class: 'chibiCategoryBtn', id: 'chibiCategoryBtn' + i, x: d.Pos.X, y: d.Pos.Y, width: d.Pos.W, height: d.Pos.H });
    bindData(btn, d);
    // 引数なしで呼ばれたら（起動の処理の d3.select("#chibiCategoryBtn0").on("click")()）先頭のカテゴリを選び直す
    fx.d3Click(btn, (dd?: unknown) => clickCategory(category, dd == undefined ? 0 : i, dd == undefined));
    roots[i].appendChild(btn);
  });
  // 選んでいるカテゴリの下に出る色の帯
  svg.appendChild(
    svgEl('rect', { class: 'chibiCategorySelected', id: 'chibiCategorySelected', display: 'none', fill: e.COLOR_ACTIVE, x: 0, y: (aH * 13) / 15, width: aW * btnExp, height: (aH * 2) / 15 }),
  );
}

/**
 * カテゴリのボタンを押したとき。別のカテゴリなら選びかえて（レイヤー・パーツの一覧・色・グループ・編集のボタン）、同じカテゴリならパーツの一覧を開け閉めする。
 * isExternal: 起動の処理から呼ばれた（同じカテゴリでも選び直し、オートセーブはしない）
 */
function clickCategory(category: ClassicCategoryData, i: number, isExternal: boolean): void {
  const e = E();
  const d = category.CATEGORY[i];
  // 選んでいる帯の位置（既定はボタンの位置。旧画面（storm など）は「ボタンの幅 × 番号」で、ボタンの余白の分だけ左に寄る）
  const btn = document.querySelector('#chibiCategoryBtn' + i);
  const currentX = e.config('CATEGORY_SEL_BY_INDEX', false) ? d.Pos.W * i : btn ? btn.getAttribute('x') : undefined;
  fx.setCss('#chibiCategorySelected', 'display', 'block');
  const bar = document.querySelector('#chibiCategorySelected');
  if (bar && currentX !== undefined && currentX !== null) bar.setAttribute('x', String(currentX));
  if (!hasClass(document.querySelector('rect#chibiCategoryBtnR' + i), e.ATTR_SELECTED) || isExternal) {
    // 別のカテゴリ: 選んでいる印を移し、入れるレイヤー（3 枚まで）を選ぶ
    document.querySelectorAll('rect.chibiCategoryBtnR').forEach((el) => removeClass(el, e.ATTR_SELECTED));
    document.querySelectorAll('rect#chibiCategoryBtnR' + i).forEach((el) => addClass(el, e.ATTR_SELECTED));
    e.selCategory = d;
    if (d.LAYER[0] == e.PARTS_BG) {
      e.selLayer = e.svgBg.select('#' + e.ATTR_LAYER + d.LAYER[0]); // 背景は背景用の SVG
      e.selLayer2 = null;
      e.selLayer3 = null;
    } else {
      e.selLayer = e.svgMake.select('#' + e.ATTR_LAYER + d.LAYER[0]);
      e.selLayer2 = d.LAYER.length >= 2 ? e.svgMake.select('#' + e.ATTR_LAYER + d.LAYER[1]) : null;
      e.selLayer3 = d.LAYER.length >= 3 ? e.svgMake.select('#' + e.ATTR_LAYER + d.LAYER[2]) : null;
    }
    // パーツの一覧・色・グループ・どれか1つのカテゴリ
    e.setPartsList(d.NAME, d.REVERSE, d.SCALE, e.partsCache[d.NAME], e.scParts.x(e.SIZE_PARTS_ICON), e.scParts.y(e.SIZE_PARTS_ICON), e.SIZE_PARTS_ROW, e.ATTR_LAYER, d.LAYER, d.TGLAYER, d);
    e.selColor(d);
    e.groupCategory = e.getGroupCategory(category.CATEGORY, d);
    e.excCategory = e.getExcludeCategory(category.CATEGORY, d);
    // 編集のボタンの有効・無効（左右・移動・反転・拡大縮小）
    e.btnEditLR.ENABLED(d.IS_SEL > 0);
    e.btnSubMove.ENABLED(d.IS_MOVE > 0);
    e.btnSubReverse.ENABLED(d.IS_REV > 0);
    e.btnSubExpand.ENABLED(d.IS_ZOOM > 0);
    e.btnSubContract.ENABLED(d.IS_ZOOM > 0);
    // 線の太さ（STROKE_WIDTH）: 変えられるカテゴリ（IS_SWIDTH）だけ太く・細くを押せる
    if (e.STROKE_WIDTH) {
      e.btnSubThick.ENABLED((d.IS_SWIDTH as number) > 0);
      e.btnSubThin.ENABLED((d.IS_SWIDTH as number) > 0);
    }
    // 第1世代の front 型（PARTS_LIST_BY_BUTTON）はパーツの一覧をメニューのボタンで出し入れするので、ここでは開かない
    if (!e.config('PARTS_LIST_BY_BUTTON', false)) {
      e.btnEditParts.ON_PARTS(true);
      // 素体のカテゴリ（PARTS_TIPS1）なら注意の吹き出し
      if (d.NAME == e.PARTS_TIPS1 && e.tips1 != null) e.tips1.show();
    }
  } else if (!e.config('PARTS_LIST_BY_BUTTON', false)) {
    // 同じカテゴリ: パーツの一覧を開け閉めし、選んでいるパーツのボタンが見える所までスクロールを戻す
    // （ボタンが見つからないと位置は NaN。代表・2 枚目のレイヤーにパーツが無く selLayer3 が null だと getSelLayerId の中で例外になる。前と同じ）
    e.btnEditParts.SW_PARTS();
    const sbar = e.DIV_F_PARTS_SC;
    let scrollPos = 0;
    if (d.NAME != e.PARTS_CHARA) {
      const pb = document.querySelector('rect.chibiPartsBtnR#pbr' + E().getSelLayerId());
      const x = pb ? pb.getAttribute('x') : null;
      scrollPos = Math.max(0, Math.round(x == null ? NaN : Number(x)));
    }
    if (!e.isMobile) fx.scrollbarTo(sbar, [0, Math.max(0, scrollPos - (fx.offsetLeft(sbar) as number))], 0);
    else fx.setScrollLeft(sbar, scrollPos);
  }
  if (!isExternal) E().saveChara(E().ATTR_AUTOSAVEKEY);
}

/** キャラのレイヤーの g（"lcart_" + レイヤーの名前）と、パーツ・色・移動・反転などの初めの値 */
function makeLayers(category: ClassicCategoryData): void {
  const e = E();
  const make = e.svgMake.node();
  if (!make) return;
  const scx = e.scMake.x(e.SIZE_CHARAT_IMAGE) / e.SIZE_CHARAT_IMAGE, scy = e.scMake.y(e.SIZE_CHARAT_IMAGE) / e.SIZE_CHARAT_IMAGE;
  category.LAYER.forEach((name) => {
    const id = e.ATTR_LAYER + name;
    const g = svgEl('g', {
      id,
      paramParts: '', // パーツ
      paramTx: 0, // 横の位置
      paramTy: 0, // 縦の位置
      paramScx: scx, // 横の倍率
      paramScy: scy, // 縦の倍率
      paramRev: 0, // 反転
      paramScale: 1, // パーツの拡大縮小
      paramCol: '', // 色
      paramColFrom: '', // グラデーションの上
      paramColTo: '', // グラデーションの下
      paramColLFrom: '', // 左の上
      paramColLTo: '', // 左の下
      paramColRFrom: '', // 右の上
      paramColRTo: '', // 右の下
      paramPtn: '', // 柄
      paramPrev: 0, // 柄の色の入れかえ
      paramPtnMode: 0, // 柄のモード
      paramRotate: 0, // 回転
      paramOpacity: 1, // うすさ
      transform: 'translate(0, 0), scale(' + scx + ',' + scy + ')',
    });
    bindData(g, id);
    make.appendChild(g);
  });
  // 線の太さ（STROKE_WIDTH のメーカーだけ）
  if (e.STROKE_WIDTH) make.querySelectorAll("g[id^='" + e.ATTR_LAYER + "']").forEach((g) => g.setAttribute('paramSW', ''));
}

/** 名前の文字（第1世代は円の縁に沿わせられる・台詞のメーカーは吹き出しの中・ほかは絵の下）とコピーライト */
function makeTexts(): void {
  const e = E();
  const nameFontSize = e.MODE_TEXT ? 46 : 25;
  const crFontSize = 12; // コピーライトの文字の大きさ
  const clip = document.querySelector('#' + e.SVG_CLIP);
  const textBox = document.querySelector('#' + e.SVG_TEXT);
  const textH = () => Number(textBox ? textBox.getAttribute('height') : NaN);
  if (e.config('NAME_ARC', false)) {
    // 第1世代（front 型など）: 円弧のパス textmark を作り、名前の text は画面ファイルの chibi.setTextArea が作る（名前の画面の「縁に沿って表示」で弧と平らを切りかえる）
    const size = e.scMake.x(e.SIZE_CHARAT_IMAGE);
    const arcMargin = size * 0.19, arcPad = size * 0.035;
    const arcX1 = 0 + (arcMargin + arcPad);
    const arcX2 = arcX1 + size - (arcMargin + arcPad) * 2;
    const arcY = e.scMake.y(e.SIZE_CHARAT_IMAGE) / 2;
    const arcR = (arcX2 - arcX1) / 2;
    if (clip) {
      const defs = svgEl('defs');
      defs.appendChild(svgEl('path', { id: 'textmark', fill: 'none', stroke: 'none', d: 'M ' + arcX1 + ' ' + arcY + ' A ' + arcR + ' ' + arcR + ' 0 0 0 ' + arcX2 + ' ' + arcY }));
      clip.appendChild(defs);
      const use = svgEl('use');
      use.setAttributeNS(XLINK, 'href', '#textmark');
      clip.appendChild(use);
    }
    e.setTextArea();
  } else if (e.MODE_TEXT) {
    // 台詞のメーカー: clip 側の SVG に名前の text
    if (clip) {
      // 幅・高さは clip の値を写す（無ければ付けない＝d3 の attr(名前, null) と同じ）
      const g = svgEl('g');
      const cw = clip.getAttribute('width'), ch = clip.getAttribute('height');
      if (cw != null) g.setAttribute('width', cw);
      if (ch != null) g.setAttribute('height', ch);
      g.setAttribute('id', e.SVG_TEXT + 'gname');
      const makeW = Number(document.querySelector('#' + e.SVG_MAKE)?.getAttribute('width'));
      const text = svgEl('text');
      text.style.setProperty('display', 'none');
      text.setAttribute('id', e.SVG_TEXT + 'name');
      text.setAttribute('x', String(makeW * (e.isMobile ? 0.5 : 0.5 * (e.SCALE_MAKE / 0.89)) - e.scMake.y(nameFontSize) * (e.isMobile ? 0.35 : 0.65)));
      text.setAttribute('y', String(Number(clip.getAttribute('height')) * (e.isMobile ? 0.27 : 0.26 * (e.SCALE_MAKE / 0.89)) - e.scMake.y(nameFontSize * 1) / 2));
      text.setAttribute('text-anchor', 'middle');
      text.setAttribute('font-family', e.FONT_NAME);
      text.setAttribute('font-size', String(e.scMake.y(nameFontSize)));
      text.setAttribute('fill', e.COLOR_NAME_WM);
      // 注意: "#" が抜けている（"FFFFFF"）ので縁取りの色として効いていない（前と同じ）
      text.setAttribute('stroke', 'FFFFFF');
      text.setAttribute('textdata', '');
      text.textContent = '';
      g.appendChild(text);
      clip.appendChild(g);
    }
  } else if (textBox) {
    // 名前: 絵の下、コピーライトの上（共通版は 2 倍の大きさで白く縁取る。第2世代 a（boys・genesis・jakigan）などは NAME_OUTLINE: false で等倍・縁取りなし）
    const outline = e.config('NAME_OUTLINE', true);
    const text = svgEl('text');
    text.style.setProperty('display', 'none');
    text.setAttribute('id', e.SVG_TEXT + 'name');
    text.setAttribute('x', String(fx.width(e.DIV_F_MAIN) / 2));
    text.setAttribute('y', String(textH() - e.scMake.y(crFontSize * 1) - e.scMake.y(nameFontSize) * 0.5));
    text.setAttribute('text-anchor', 'middle');
    text.setAttribute('font-family', e.FONT_NAME);
    text.setAttribute('font-size', String(e.scMake.y(nameFontSize) * (outline ? 2 : 1)));
    text.setAttribute('fill', e.COLOR_NAME);
    if (outline) {
      text.setAttribute('stroke', '#FFFFFF');
      text.setAttribute('stroke-width', '1');
    }
    text.setAttribute('textdata', '');
    text.textContent = '';
    textBox.appendChild(text);
  }
  // コピーライト（表記は COPYRIGHT_HOLDER。第2世代 a は "CHARAT"）
  if (textBox) {
    const cr = svgEl('text', {
      id: e.SVG_TEXT + 'copyright',
      x: fx.width(e.DIV_F_MAIN) / 2,
      y: textH() - e.scMake.y(crFontSize * 0.5),
      'text-anchor': 'middle',
      'font-family': e.FONT_CR,
      'font-size': e.scMake.y(crFontSize),
      fill: e.COLOR_CR,
    });
    cr.textContent = '(C)' + new Date().getFullYear() + ' ' + e.config('COPYRIGHT_HOLDER', 'CHARAT / LIBRE, Inc.');
    textBox.appendChild(cr);
  }
}

/** 保存用の SVG（本体・背景）の defs と、本体に名前の書体。表示の切り抜き（MODE_CLIP）・手描き風のフィルター（MODE_FILTER） */
function makeDefs(): void {
  const e = E();
  const save = document.querySelector('#' + e.SVG_SAVE);
  const bgSave = document.querySelector('#' + e.SVG_BGSAVE);
  const make = document.querySelector('#' + e.SVG_MAKE);
  const clipdef = save ? save.appendChild(svgEl('defs')) : null;
  if (bgSave) bgSave.appendChild(svgEl('defs'));
  if (!clipdef) return;
  const style = svgEl('style');
  style.textContent = "@font-face { font-family: '" + e.FONT_NAME + "'; src: url('" + e.FONT_NAME_SRC + "') format('opentype');}";
  clipdef.appendChild(style);
  // 表示の切り抜き（500 の絵の枠より上下左右 3% 広い四角）
  if (e.MODE_CLIP) {
    const cwmgn = e.scMake.x(e.SIZE_CHARAT_IMAGE) * 0.03, chmgn = e.scMake.y(e.SIZE_CHARAT_IMAGE) * 0.03;
    const cp = svgEl('clipPath', { id: e.SVG_CLIP + 'rect' });
    cp.appendChild(svgEl('rect', { x: 0 - cwmgn / 2, y: 0 - chmgn / 2, width: e.scMake.x(e.SIZE_CHARAT_IMAGE) + cwmgn, height: e.scMake.y(e.SIZE_CHARAT_IMAGE) + chmgn }));
    clipdef.appendChild(cp);
    if (make) make.setAttribute('clip-path', 'url(#' + e.SVG_CLIP + 'rect)');
  }
  // ノイズで輪郭を少し揺らす手描き風のフィルター
  if (e.MODE_FILTER) {
    const filter = svgEl('filter', { id: e.SVG_CLIP + 'filter' });
    filter.appendChild(svgEl('feTurbulence', { type: 'fractalNoise', baseFrequency: '0.8', numOctaves: '1', result: 'turbulence' }));
    filter.appendChild(svgEl('feDisplacementMap', { in: 'SourceGraphic', in2: 'turbulence', scale: '1.5' }));
    clipdef.appendChild(filter);
    if (make) make.setAttribute('filter', 'url(#' + e.SVG_CLIP + 'filter)');
  }
}
